import { FC, useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
} from '@mui/material'
import type { Parceiro, ParceiroRequest, TipoParceiro } from '@/types/parceiro'
import { TIPOS_PARCEIRO } from '@/types/parceiro'
import type { Produto } from '@/types/produto'
import { cnpjValido, mascararCnpj, normalizarCnpj } from '@/utils/cnpj'

interface Props {
  aberto: boolean
  /** Sem parceiro = criar; com parceiro = editar (o status não passa por aqui). */
  parceiro: Parceiro | null
  produtos: Produto[]
  onFechar: () => void
  onSalvar: (request: ParceiroRequest) => Promise<void>
}

const vazio: ParceiroRequest = {
  nome: '',
  tipo: 'BANCO',
  cnpj: '',
  contato: '',
  email: '',
  telefone: '',
  produtosOferecidos: [],
}

const deParceiro = (p: Parceiro): ParceiroRequest => ({
  nome: p.nome,
  tipo: p.tipo,
  cnpj: p.cnpj,
  contato: p.contato ?? '',
  email: p.email,
  telefone: p.telefone ?? '',
  produtosOferecidos: p.produtosOferecidos ?? [],
})

/**
 * Formulário de cadastro/edição do parceiro (A12, Onda 7.1). Só os dados cadastrais e os
 * produtos que ele opera; a política por produto continua no `PoliticaParceiroDialog`.
 */
const ParceiroFormDialog: FC<Props> = ({ aberto, parceiro, produtos, onFechar, onSalvar }) => {
  const [form, setForm] = useState<ParceiroRequest>(vazio)
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  useEffect(() => {
    if (aberto) {
      setForm(parceiro ? deParceiro(parceiro) : vazio)
      setErro(null)
    }
  }, [aberto, parceiro])

  const campo = (chave: keyof ParceiroRequest, valor: string | string[]) =>
    setForm((f) => ({ ...f, [chave]: valor }))

  const cnpjLimpo = normalizarCnpj(form.cnpj)
  const cnpjOk = cnpjValido(cnpjLimpo)
  const valido = form.nome.trim().length > 0 && cnpjOk && form.email.trim().length > 0

  const salvar = async () => {
    setSalvando(true)
    setErro(null)
    try {
      await onSalvar({
        ...form,
        nome: form.nome.trim(),
        cnpj: cnpjLimpo,
        email: form.email.trim(),
        contato: form.contato?.trim() || null,
        telefone: form.telefone?.trim() || null,
      })
      onFechar()
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível salvar o parceiro')
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Dialog open={aberto} onClose={salvando ? undefined : onFechar} maxWidth="sm" fullWidth>
      <DialogTitle>{parceiro ? 'Editar parceiro' : 'Novo parceiro'}</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2, pt: '8px !important' }}>
        {erro && <Alert severity="error">{erro}</Alert>}
        <TextField
          label="Nome"
          value={form.nome}
          onChange={(e) => campo('nome', e.target.value)}
          required
          autoFocus
          inputProps={{ 'aria-label': 'Nome do parceiro' }}
        />
        <FormControl>
          <InputLabel id="parceiro-tipo">Modelo</InputLabel>
          <Select
            labelId="parceiro-tipo"
            label="Modelo"
            value={form.tipo}
            onChange={(e) => campo('tipo', e.target.value as TipoParceiro)}
          >
            {(Object.keys(TIPOS_PARCEIRO) as TipoParceiro[]).map((t) => (
              <MenuItem key={t} value={t}>
                {TIPOS_PARCEIRO[t]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="CNPJ"
          value={form.cnpj}
          onChange={(e) => campo('cnpj', mascararCnpj(e.target.value))}
          required
          error={cnpjLimpo.length === 14 && !cnpjOk}
          helperText={
            cnpjLimpo.length === 14 && !cnpjOk
              ? 'CNPJ inválido: confira os dígitos verificadores'
              : 'Numérico ou alfanumérico, com ou sem pontuação'
          }
          inputProps={{ 'aria-label': 'CNPJ do parceiro' }}
        />
        <TextField
          label="E-mail"
          type="email"
          value={form.email}
          onChange={(e) => campo('email', e.target.value)}
          required
          inputProps={{ 'aria-label': 'E-mail do parceiro' }}
        />
        <TextField
          label="Contato"
          value={form.contato ?? ''}
          onChange={(e) => campo('contato', e.target.value)}
        />
        <TextField
          label="Telefone"
          value={form.telefone ?? ''}
          onChange={(e) => campo('telefone', e.target.value)}
        />
        <FormControl>
          <InputLabel id="parceiro-produtos">Produtos que opera</InputLabel>
          <Select
            labelId="parceiro-produtos"
            multiple
            value={form.produtosOferecidos ?? []}
            onChange={(e) => {
              const v = e.target.value
              campo('produtosOferecidos', typeof v === 'string' ? v.split(',') : v)
            }}
            input={<OutlinedInput label="Produtos que opera" />}
            renderValue={(ids) =>
              (ids as string[])
                .map((id) => produtos.find((p) => p.id === id)?.nome ?? id)
                .join(', ')
            }
          >
            {produtos.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                <Checkbox checked={(form.produtosOferecidos ?? []).includes(p.id)} />
                <ListItemText primary={p.nome} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onFechar} disabled={salvando}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={salvar} disabled={!valido || salvando}>
          {salvando ? 'Salvando…' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ParceiroFormDialog
